import { useState, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import difference from 'lodash/difference';

export const sortOptions = (values: Option[]): Option[] =>
    values.toSorted((a, b) => {
        if (a.isDefault) return -1;
        if (b.isDefault) return 1;

        if (a.disabled && b.disabled) return a.value.localeCompare(b.value);
        if (a.disabled) return -1;
        if (b.disabled) return 1;

        return a.value.localeCompare(b.value);
    });

export interface Option {
    value: string;
    label: ReactNode;
    disabled: boolean;
    secondaryText: ReactNode;
    indent: boolean;
    inactive: boolean;
    isDefault?: boolean;
    isSelected?: boolean;
}

interface UseOptionTabsProps {
    optionsList: Option[];
    fieldPathKey: string;
}

export const useOptionTabs = ({ optionsList, fieldPathKey }: UseOptionTabsProps) => {
    const { getValues, unregister, register } = useFormContext();

    const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
    const [activeTab, setActiveTab] = useState<string>('');

    const registeredItems = Object.keys(getValues(fieldPathKey) || {});

    const toggleSelection = (selectedValues: string[]) => {
        if (!optionsList) return [];

        const updatedOptions = optionsList.map((option) => ({
            ...option,
            isSelected: selectedValues.includes(option.value),
        }));

        const filteredSelectedOptions = updatedOptions.filter((option) => option.isSelected || option.isDefault);

        const sortedOptions = sortOptions(filteredSelectedOptions);

        const missingToRegister = difference(selectedValues, registeredItems);
        const missingToUnregister = difference(registeredItems, selectedValues);

        missingToRegister.forEach((value) => {
            register(`${fieldPathKey}.${value}`);
            if (!activeTab) {
                setActiveTab(value);
            }
        });

        missingToUnregister.forEach((value) => {
            unregister(`${fieldPathKey}.${value}`);
            if (value === activeTab) {
                setActiveTab(selectedValues.at(-1) || '');
            }
        });

        setSelectedOptions(sortedOptions);
    };

    return {
        activeTab,
        selectedOptions,
        setActiveTab,
        setSelectedOptions,
        toggleSelection,
    };
};
